import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from '@studio-freight/lenis';
import CustomCursor from './components/CustomCursor';
import EarlyAccessForm from './components/EarlyAccessForm';
import Footer from './components/Footer';
import Hero from './components/Hero';
import Navbar from './components/Navbar';
import PlatformPillars from './components/PlatformPillars';
import UseCaseCards from './components/UseCaseCards';

gsap.registerPlugin(ScrollTrigger);

export default function App() {
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // Smooth scroll with Lenis
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });

    function raf(time: number) {
      lenis.raf(time);
      rafRef.current = requestAnimationFrame(raf);
    }
    rafRef.current = requestAnimationFrame(raf);

    // Parallax orbs
    const orbs = document.querySelectorAll('.background-orb');
    orbs.forEach((orb, i) => {
      const speed = i === 0 ? 0.3 : i === 1 ? 0.2 : 0.15;
      gsap.to(orb, {
        y: () => -100 * speed,
        ease: 'none',
        scrollTrigger: {
          trigger: '.page-shell',
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1.5,
        },
      });
    });

    // Grid fade effect
    const grid = document.querySelector('.background-grid');
    if (grid) {
      gsap.fromTo(
        grid,
        { opacity: 0.03 },
        {
          opacity: 0.08,
          scrollTrigger: {
            trigger: '#platform',
            start: 'top center',
            end: 'bottom center',
            scrub: true,
          },
        }
      );
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      lenis.destroy();
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  return (
    <div className="page-shell">
      <CustomCursor />
      <div className="background-grid" aria-hidden="true" />
      <div className="background-orb orb-1 left-[-8rem] top-40 h-72 w-72 bg-rose-200/40" aria-hidden="true" />
      <div className="background-orb orb-2 right-[-7rem] top-20 h-80 w-80 bg-sky-200/40" aria-hidden="true" />
      <div className="background-orb orb-3 left-[30%] top-[60%] h-96 w-96 bg-violet-200/30" aria-hidden="true" />
      <Navbar />
      <main>
        <Hero />
        <PlatformPillars />
        <UseCaseCards />
        <EarlyAccessForm />
      </main>
      <Footer />
    </div>
  );
}
